// The plan's selection grips: each selected object's grips, the press that arms
// one, the step and its typed figure, its end. docs/claude/selection.md.
import {
  type Connection,
  type Vec2,
} from '../../shared/types';
import {
  MULTI_KINDS,
  selectedCount,
} from '../selection';
import { type CeilingNote } from '../ceiling';
import { endDragEdit } from '../connDrag';
import {
  connEdit,
  planBendDrag,
} from './connectionUi';
import {
  dropDrag,
  flashToolStatus,
  invalidate,
  renderProperties,
  state,
  updateStatus,
  vp,
} from './core';
import {
  arcGripPoints,
  arcThroughPoints,
} from '../geometry';
import {
  GRIP_GLUED,
  GRIP_HIT_PX,
} from '../grip';
import { DECIDE_PX } from '../handles';
import { tr } from '../i18n';
import {
  backrestGrips,
  channelGrips,
  marginHandleSky,
  pickedLightsNow,
  skyMarginGrips,
  stripEndGrips,
} from './lightTools';
import { type StepEnd } from '../undoHistory';
import { type UnderWayRow } from '../underWay';
import {
  dragPinnedVertex,
  polylineIsClosed,
} from '../wallTool';
import {
  canvas,
  connectionPath,
  connPickAt,
  connPortsAt,
  editVertexSnap,
  hitHandle,
  holdSections,
  screenPos,
  selectedRefs,
  selectedRoomFeatureAt,
  type UndoMark,
  undoSteps,
} from '../main';
import { activeSheet } from './sheetSpace';
import {
  ceilingGrips,
  ceilingRoom,
  flashOnce,
} from './ceilingUi';
import { selectedFixtureNow } from './roomFixtures';
import {
  cageGrips,
  sculptEndGrips,
} from './sculptBenchUi';
import {
  parseEntry,
  updateDistEntry,
} from './distanceEntry';
import { parseMeasure } from './panelRows';
import { refKey } from '../pickStack';

/** This module's state that main.ts reads and writes too. */
export const gripUi = {
  gripWork: null as GripWork | null,
};

/** A grip of the one selected leader, arc or line: where it sits (world), the
 *  points it aligns to while dragged, the neighbour a 45° / ortho step is
 *  measured from, and what dragging it to `p` does. Built fresh at the press, so
 *  an arc grip's `apply` keeps the OTHER two grips where they were when grabbed. */
export interface SelGrip {
  at: Vec2; others: Vec2[]; prev?: Vec2; apply: (p: Vec2) => void;
  /** The object the grip belongs to — left out of its own snap. */
  self?: string;
  /** Takes the raw cursor instead of the vertex snap (a connection end re-glues
   *  through the Connect tool's own pick); `free` is Alt. */
  drag?: (world: Vec2, free: boolean) => void;
  fill?: string;
  /** Gives way to the selected room's resize handle, door, pane or opening under
   *  the press (a truncated pyramid's setback grip lands on them). */
  yields?: boolean;
  /** The room a drag of it reshapes the ceiling of: its section views wait for
   *  the drag to settle (`holdSections`). */
  holds?: string;
  /** A grip set by ONE figure — a gable's ridge (Ridge from centre), a flat edge
   *  (Setback) — takes it typed; any other grip takes a typed point. */
  figure?: { label: string; set: (v: number) => CeilingNote | null };
  /** The figure drawn while it is worked (world): what it sets, measured. */
  measure?: () => { a: Vec2; b: Vec2; len: number } | null;
}

/** The grips of the selection — the ONE list the renderer draws and the press
 *  grabs, so a grip can't be drawn where nothing answers or answer where nothing
 *  is drawn. AutoCAD's: a leader's and a line's vertices; an arc's two ends and
 *  the middle of its sweep. (Shapes, parts, rooms and text boxes keep their own
 *  handles; a thick wall reshapes in vertex edit.) */
export function selectionGrips(): SelGrip[] {
  if (activeSheet() || state.editVerticesId) return [];
  // The Starry sky tool works its sky's margin grips.
  if (state.tool === 'light-sky') { const k = marginHandleSky(); return k ? skyMarginGrips(k.room, k.sky) : []; }
  if (state.tool !== 'select') return [];
  const vertexGrips = (pts: Vec2[], self?: string): SelGrip[] => pts.map((p, i) => ({
    at: p, others: pts.filter((_, j) => j !== i), prev: pts[i - 1] ?? pts[i + 1], self,
    apply: (q: Vec2) => { pts[i] = q; },
  }));
  const conn = state.selectedConnId ? state.drawing.connections.find((c) => c.id === state.selectedConnId) : undefined;
  if (conn) return connectionGrips(conn);
  if (selectedCount(state, MULTI_KINDS) !== 1) return [];
  const leader = state.selectedLeaderId ? (state.drawing.leaders || []).find((l) => l.id === state.selectedLeaderId) : undefined;
  if (leader) return vertexGrips(leader.points, leader.id);
  const wall = state.selectedWallId ? (state.drawing.walls || []).find((w) => w.id === state.selectedWallId) : undefined;
  if (wall && wall.style === 'line') {
    // A line's ends close it when one is dropped on the other, and a closed line's
    // joint (two stacked points) moves as one — its twin is no snap target.
    const pts = wall.points, last = pts.length - 1, closed = polylineIsClosed(pts);
    return vertexGrips(pts, wall.id).map((g, i) => ({
      ...g,
      others: closed && (i === 0 || i === last) ? g.others.filter((v) => v !== pts[i === 0 ? last : 0]) : g.others,
      apply: (q: Vec2) => { dragPinnedVertex(wall, i, q, closed, 12 / vp.scale); },
    }));
  }
  const arc = state.selectedArcId ? (state.drawing.arcs || []).find((a) => a.id === state.selectedArcId) : undefined;
  if (arc) {
    const g = arcGripPoints(arc);
    const set = (p0: Vec2, p1: Vec2, p2: Vec2): void => { const fit = arcThroughPoints(p0, p1, p2); if (fit) Object.assign(arc, fit); };
    return [
      { at: g.start, others: [g.mid, g.end], prev: g.mid, self: arc.id, apply: (q) => set(q, g.mid, g.end) },
      { at: g.mid, others: [g.start, g.end], self: arc.id, apply: (q) => set(g.start, q, g.end) },
      { at: g.end, others: [g.start, g.mid], prev: g.mid, self: arc.id, apply: (q) => set(g.start, g.mid, q) },
    ];
  }
  const sky = marginHandleSky();
  if (sky) return skyMarginGrips(sky.room, sky.sky);
  const strip = selectedFixtureNow();
  if (strip?.backrest) return backrestGrips(strip.inst, strip.backrest);
  if (strip?.sculpted) return [...sculptEndGrips(strip.inst, strip.sculpted), ...cageGrips(strip.inst, strip.sculpted)];
  if (strip?.light?.kind === 'strip') return stripEndGrips(strip.inst, strip.light);
  if (strip?.light?.kind === 'channel' && !pickedLightsNow()) return channelGrips(strip.inst, strip.light);
  const room = ceilingRoom();
  if (room) return ceilingGrips(room);
  return [];
}

/** A selected connection's grips: its two ends (green while glued) and its bends.
 *  Dragging an end re-glues it wherever the Connect tool would — another object,
 *  one of its points, or a free point (Alt). */
function connectionGrips(c: Connection): SelGrip[] {
  const route = connectionPath(c);
  if (!route) return [];
  const end = (which: 'from' | 'to'): SelGrip => {
    const at = which === 'from' ? route[0] : route[route.length - 1];
    const glued = !!(which === 'from' ? c.from : c.to);
    return {
      at, others: [], fill: glued ? GRIP_GLUED : undefined, apply: () => {},
      drag: (world, free) => {
        const pk = connPickAt(world, free);
        connEdit(c, endDragEdit(which, pk));
        state.connHoverDots = pk.id ? connPortsAt(world) : [];
      },
    };
  };
  // A bend's grip moves it where the plan's snaps and its neighbours put it
  // (`planBendDrag`), keeping what else it holds — a pipe's level among it.
  const bends = (c.waypoints ?? []).map((w, i): SelGrip => ({
    at: w, others: [], self: c.id, apply: () => {},
    drag: (world, free) => planBendDrag(c, i, world, free),
  }));
  return [end('from'), ...bends, end('to')];
}

/** The selection grip under a screen point — the NEAREST, so two close grips
 *  are both reachable —, or null; with its place in `selectionGrips()`. */
export function gripAt(sp: Vec2): (SelGrip & { index: number }) | null {
  const grips = selectionGrips();
  let best: (SelGrip & { index: number }) | null = null, bestD = Infinity;
  for (let index = 0; index < grips.length; index++) {
    const g = grips[index], s = vp.worldToScreen(g.at);
    const dx = Math.abs(s.x - sp.x), dy = Math.abs(s.y - sp.y);
    if (dx > GRIP_HIT_PX || dy > GRIP_HIT_PX) continue;
    if (g.yields && (hitHandle(sp) || selectedRoomFeatureAt(vp.screenToWorld(sp)))) continue;
    const d = Math.hypot(dx, dy);
    if (d < bestD) { bestD = d; best = { ...g, index }; }
  }
  return best;
}

/** What the worked grip belongs to: a change of tool, view or selection ends its work. */
export function gripOwner(): string {
  return `${state.tool}|${state.viewTab}|${state.editVerticesId ?? ''}|${selectedRefs().map(refKey).join(',')}`;
}

/** Whether the worked grip is still the selection's; when not, its work ends —
 *  kept, or (its drawing replaced, by a load) just dropped. */
export function gripWorkLive(gw: GripWork): boolean {
  if (!undoSteps.live(gw.start)) { dropGripWork(); return false; }
  if (gripOwner() !== gw.owner) { finishGripWork(); return false; }
  return true;
}

/** Apply the pointer to the worked grip, once it has gone `DECIDE_PX` from the
 *  press: the snap a vertex drag takes (objects, then alignment to the object's
 *  other grips and 45° / ortho from the neighbour; Alt suspends), or the grip's
 *  own drag. */
export function stepGripWork(): void {
  const gw = gripUi.gripWork;
  if (!gw || !gripWorkLive(gw)) return;
  if (!gw.moved && Math.hypot(gw.pointer.x - gw.press.x, gw.pointer.y - gw.press.y) < DECIDE_PX) return;
  gw.moved = true;
  const g = gw.grip, world = vp.screenToWorld(gw.pointer);
  if (g.holds) holdSections(g.holds);
  if (g.drag) g.drag(world, gw.alt);
  else g.apply(editVertexSnap(world, g.others, g.self ?? null, g.prev, gw.alt));
  invalidate();
}

/** Enter on a typed entry while a grip is worked: a one-figure grip takes its
 *  figure (parseMeasure), any other a point — `x,y`, `@dx,dy`, `d<angle`, or a
 *  distance toward the pointer once it has moved — from where it stood at the
 *  press, exactly (no snap); then it is done. */
export function commitGripEntry(): void {
  const gw = gripUi.gripWork, text = state.distEntry;
  state.distEntry = null;
  updateDistEntry();
  if (!gw || text == null || !gripWorkLive(gw)) return;
  const g = gw.grip;
  if (g.figure) {
    const v = parseMeasure(text);
    if (v == null) { flashToolStatus(tr('Type the figure in mm, or with a unit (cm / m / ft / in).')); return; }
    flashOnce(g.figure.set(v));
  } else {
    const at = vp.screenToWorld(gw.pointer);
    const p = parseEntry(text, gw.base, gw.moved ? { x: at.x - gw.base.x, y: at.y - gw.base.y } : null);
    if (!p) { flashToolStatus(tr('Type a point: x,y — @dx,dy — distance<angle — or, once it has moved, a distance toward the pointer.')); return; }
    if (g.drag) g.drag(p, true); else g.apply(p);
  }
  finishGripWork();
}

/** A press's work — a grip's, a handle's — ends, however it ends: its slot
 *  cleared, its step ended from the press (`undoSteps.end`: kept as one step when
 *  it changed anything and no push inside it, a Delete, recorded it already; put
 *  back; or, on a drawing replaced under it, just let go), then its own
 *  after-work. False when none was under way. */
export function endPressWork<W extends { start: UndoMark }>(w: W | null, how: StepEnd, clear: () => void, after: (w: W) => void): boolean {
  if (!w) return false;
  clear();
  undoSteps.end(how, w.start);
  after(w);
  return true;
}

function endGripWork(how: StepEnd): boolean {
  return endPressWork(gripUi.gripWork, how, () => { gripUi.gripWork = null; }, afterGripWork);
}
/** The worked grip is done: one undo step, from the press, when it changed anything. */
export function finishGripWork(): void { endGripWork('keep'); }
/** Esc (or undo): the worked grip goes back where it was, nothing recorded. True
 *  when one was worked. */
export function cancelGripWork(): boolean { return endGripWork('cancel'); }
/** The drawing was replaced under a worked grip (a load): its work just ends. */
export function dropGripWork(): void { endGripWork('drop'); }

function afterGripWork(): void {
  holdSections(null);
  if (state.dragMode === 'grip') dropDrag();
  if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
  state.connHoverDots = [];
  state.snapIndicator = null;
  state.editGuide = null;
  canvas.style.cursor = '';
  renderProperties(); // the panel shows the new figures
  updateStatus();
  invalidate();
}

/** The prompt while a grip is worked. */
export function gripPrompt(gw: GripWork): string {
  const f = gw.grip.figure;
  if (f) {
    return (gw.armed
      ? tr('{label} — move to set it, click to drop it, or type a figure and press Enter (Esc cancels)')
      : tr('{label} — drag to set it, or type a figure and press Enter (Esc cancels)')).replace('{label}', tr(f.label));
  }
  return gw.armed
    ? tr('Grip — move it and click to drop it, or type a point (x,y · @dx,dy · distance · distance<angle) and press Enter (Esc cancels)')
    : tr('Grip — drag it, or type a point (x,y · @dx,dy · distance · distance<angle) and press Enter (Esc cancels)');
}

/** A selection grip being worked (see selectionGrips): held from its press; a
 *  release that never went `DECIDE_PX` arms it instead — it follows the pointer
 *  until a click drops it. A typed figure (a one-figure grip) or point (any other)
 *  and Enter set it; Esc puts it back. */
interface GripWork {
  grip: SelGrip;
  /** Its place in `selectionGrips()`, where it is drawn lit. */
  index: number;
  /** Where it stood at the press (world): what a typed point is measured from. */
  base: Vec2;
  /** Screen px of the press and of the pointer now. */
  press: Vec2;
  pointer: Vec2;
  alt: boolean;
  /** It has gone `DECIDE_PX` from the press: it follows the pointer. */
  moved: boolean;
  armed: boolean;
  /** Its step, begun at the press (a cancel puts the drawing back there; a drawing
   *  replaced under it ends the work), and the selection it works on — a change
   *  of that ends the work too. */
  start: UndoMark;
  owner: string;
}

/** A grip's work in the gesture under way (underWay.ts): held (DragMode `grip`,
 *  whose moves and release are its DRAG_LIFE row's) or armed — then the pointer
 *  moves it and a press drops it where it is pressed. Esc, Undo and a tab switch
 *  put it back; Enter, Space and a right-click end it where it is. */
export const GRIP_UNDER_WAY: UnderWayRow = {
  live: () => !!gripUi.gripWork,
  armed: () => !!gripUi.gripWork?.armed,
  space: () => 'plan',
  move: (p) => {
    const gw = gripUi.gripWork;
    if (!gw?.armed || p.space !== 'plan') return false;
    gw.pointer = p.at;
    gw.alt = p.alt;
    stepGripWork();
    return true;
  },
  press: (p) => {
    const gw = gripUi.gripWork;
    if (!gw?.armed || p.space !== 'plan') return false;
    gw.pointer = p.at;
    gw.alt = p.alt;
    stepGripWork();
    finishGripWork();
    return true;
  },
  finish: () => endGripWork('keep'),
  cancel: () => endGripWork('cancel'),
  drop: dropGripWork,
  check: () => { if (gripUi.gripWork) gripWorkLive(gripUi.gripWork); },
  typed: commitGripEntry,
  hint: () => (gripUi.gripWork ? gripPrompt(gripUi.gripWork) : null),
};

/** A grip's drag let go (DRAG_LIFE `grip`'s end): released where it was pressed
 *  it is armed (its gesture still open); otherwise it is done. */
export function endGripDrag(e: MouseEvent): 'own' | void {
  const gw = gripUi.gripWork;
  if (!gw || gw.armed) return;
  gw.pointer = screenPos(e);
  gw.alt = e.altKey;
  if (!gw.moved && Math.hypot(gw.pointer.x - gw.press.x, gw.pointer.y - gw.press.y) < DECIDE_PX) {
    gw.armed = true;
    dropDrag();
    state.snapIndicator = null;
    canvas.style.cursor = 'move';
    updateStatus();
    invalidate();
    return 'own';
  }
  stepGripWork();
  finishGripWork();
}
